import { MetricCard } from "@project-mgmt/ui";
import { useDashboardMetrics } from "@project-mgmt/feature-dashboard";

interface WorkspaceMetricsProps {
  workspaceId: string;
  className?: string;
}

export function WorkspaceMetrics({ workspaceId, className = "" }: WorkspaceMetricsProps) {
  const { data, isLoading, error } = useDashboardMetrics(workspaceId);

  if (isLoading) {
    return (
      <div className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-4 ${className}`}>
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-32 animate-pulse rounded-lg bg-gray-100" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg border border-red-200 bg-red-50 p-4 ${className}`}>
        <p className="text-sm text-red-600">Failed to load workspace metrics</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const metrics = data;

  // Calculate completion rate
  const completionRate = metrics.totalTasks > 0 ? Math.round((metrics.completedTasks / metrics.totalTasks) * 100) : 0;

  // Calculate cost variance percentage
  const costVariancePercentage =
    metrics.estimatedCost > 0 ? Math.round((metrics.costVariance / metrics.estimatedCost) * 100) : 0;

  // Determine trend direction for cost variance
  const costTrend: "up" | "down" | "neutral" =
    metrics.costVariance > 0 ? "up" : metrics.costVariance < 0 ? "down" : "neutral";

  return (
    <div className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-4 ${className}`}>
      {/* Active Projects */}
      <MetricCard
        title="Active Projects"
        value={metrics.activeProjects}
        subtitle={`${metrics.totalProjects} total projects`}
        icon={
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
        }
      />

      {/* Task Completion */}
      <MetricCard
        title="Task Completion"
        value={`${completionRate}%`}
        subtitle={`${metrics.completedTasks} of ${metrics.totalTasks} tasks`}
        trend={
          metrics.overdueTasks > 0
            ? {
                direction: "down",
                value: `${metrics.overdueTasks} overdue`,
              }
            : undefined
        }
        icon={
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
        }
      />

      {/* Total Investment */}
      <MetricCard
        title="Total Investment"
        value={`$${metrics.totalInvestment.toLocaleString()}`}
        subtitle={`Est. cost: $${metrics.estimatedCost.toLocaleString()}`}
        icon={
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        }
      />

      {/* Cost Variance */}
      <MetricCard
        title="Cost Variance"
        value={`$${Math.abs(metrics.costVariance).toLocaleString()}`}
        subtitle={metrics.costVariance >= 0 ? "Over budget" : "Under budget"}
        trend={{
          direction: costTrend,
          value: `${Math.abs(costVariancePercentage)}%`,
        }}
        icon={
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        }
      />
    </div>
  );
}
